/**
 * `Splitter` (W1-12 ui-kit-i18n; contract SplitterProps): panes with draggable dividers. `orientation`
 * 'horizontal' lays panes side by side (vertical dividers), 'vertical' stacks them. Sizes are percentages of
 * the container (normalised to sum to 100); `onResize` receives the new sizes live while dragging and on every
 * key press. Each divider is a focusable role="separator" (aria-valuenow = size of the pane before it): arrows
 * move it by 1 % (Shift: 10 %), Home / End to the limits, double-click or Enter shares the two panes equally.
 * Panes never shrink below `minSize` percent (default 5).
 */

import {
  Children,
  useId,
  useRef,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useT } from '../i18n/useT';
import { cx } from './internal/cx';
import s from './Splitter.module.css';
import type { SplitterProps } from './types';
import { m } from './ui.messages';

const DIVIDER_PX = 6;

/** Sizes for `count` panes: positive, summing to 100 (missing or invalid entries share the rest equally). */
export function normaliseSizes(sizes: readonly number[], count: number): number[] {
  if (count <= 0) return [];
  const raw = Array.from({ length: count }, (_, i) => {
    const v = sizes[i];
    return v !== undefined && Number.isFinite(v) && v > 0 ? v : 0;
  });
  const known = raw.filter((v) => v > 0);
  const knownSum = known.reduce((a, b) => a + b, 0);
  const missing = count - known.length;
  const fill = missing === 0 ? 0 : Math.max(100 - knownSum, missing) / missing;
  const filled = raw.map((v) => (v > 0 ? v : fill));
  const total = filled.reduce((a, b) => a + b, 0);
  return filled.map((v) => (v / total) * 100);
}

/** Moves divider `i` (between panes i and i+1) to give pane i `target` percent, within the min sizes. */
export function moveDivider(sizes: readonly number[], i: number, target: number, minSize: number): number[] {
  const a = sizes[i];
  const b = sizes[i + 1];
  if (a === undefined || b === undefined) return [...sizes];
  const pair = a + b;
  const min = Math.min(minSize, pair / 2);
  const next = Math.min(pair - min, Math.max(min, target));
  const out = [...sizes];
  out[i] = next;
  out[i + 1] = pair - next;
  return out;
}

interface Drag {
  readonly index: number;
  readonly pointerId: number;
  readonly start: number;
  readonly sizes: readonly number[];
  readonly available: number;
}

export function Splitter(
  props: SplitterProps & { readonly minSize?: number; readonly className?: string | undefined },
) {
  const t = useT();
  const baseId = useId();
  const container = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const panes = Children.toArray(props.children);
  const sizes = normaliseSizes(props.sizes, panes.length);
  const horizontal = props.orientation === 'horizontal';
  const minSize = props.minSize ?? 5;

  const template = sizes
    .flatMap((v, i) =>
      i === 0 ? [`minmax(0, ${String(v)}fr)`] : [`${String(DIVIDER_PX)}px`, `minmax(0, ${String(v)}fr)`],
    )
    .join(' ');
  const style: CSSProperties = horizontal ? { gridTemplateColumns: template } : { gridTemplateRows: template };

  const resize = (next: number[]): void => {
    props.onResize(next.map((v) => Math.round(v * 1000) / 1000));
  };

  const onPointerDown = (index: number) => (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = container.current?.getBoundingClientRect();
    const total = rect === undefined ? 0 : horizontal ? rect.width : rect.height;
    const available = total - DIVIDER_PX * (panes.length - 1);
    if (available <= 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { index, pointerId: e.pointerId, start: horizontal ? e.clientX : e.clientY, sizes, available };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (d?.pointerId !== e.pointerId) return;
    const delta = (((horizontal ? e.clientX : e.clientY) - d.start) / d.available) * 100;
    const before = d.sizes[d.index] ?? 0;
    resize(moveDivider(d.sizes, d.index, before + delta, minSize));
  };
  const onPointerEnd = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if (drag.current?.pointerId !== e.pointerId) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const equalise = (index: number): void => {
    const a = sizes[index] ?? 0;
    const b = sizes[index + 1] ?? 0;
    resize(moveDivider(sizes, index, (a + b) / 2, minSize));
  };
  const onKeyDown = (index: number) => (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const current = sizes[index] ?? 0;
    const step = e.shiftKey ? 10 : 1;
    const less = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const more = horizontal ? 'ArrowRight' : 'ArrowDown';
    let target: number | null = null;
    if (e.key === less) target = current - step;
    else if (e.key === more) target = current + step;
    else if (e.key === 'Home') target = 0;
    else if (e.key === 'End') target = Number.POSITIVE_INFINITY;
    else if (e.key === 'Enter') {
      e.preventDefault();
      equalise(index);
      return;
    }
    if (target === null) return;
    e.preventDefault();
    resize(moveDivider(sizes, index, target, minSize));
  };

  return (
    <div
      ref={container}
      className={cx(s.splitter, horizontal ? s.horizontal : s.vertical, props.className)}
      style={style}
      data-orientation={props.orientation}
    >
      {panes.map((pane, i) => {
        const paneId = `${baseId}-pane-${String(i)}`;
        const before = sizes[i] ?? 0;
        const after = sizes[i + 1] ?? 0;
        const min = Math.min(minSize, (before + after) / 2);
        return [
          <div key={`pane-${String(i)}`} id={paneId} className={s.pane}>
            {pane}
          </div>,
          i < panes.length - 1 ? (
            <div
              key={`divider-${String(i)}`}
              role="separator"
              tabIndex={0}
              className={s.divider}
              aria-orientation={horizontal ? 'vertical' : 'horizontal'}
              aria-controls={paneId}
              aria-label={t(m['splitter.resize'])}
              aria-valuemin={Math.round(min)}
              aria-valuemax={Math.round(before + after - min)}
              aria-valuenow={Math.round(before)}
              onPointerDown={onPointerDown(i)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerCancel={onPointerEnd}
              onDoubleClick={() => {
                equalise(i);
              }}
              onKeyDown={onKeyDown(i)}
            />
          ) : null,
        ];
      })}
    </div>
  );
}
